import { useStore } from "react-redux";
import { useLocation } from 'react-router';
import { useContext, useCallback } from "react";
import ToolContext, { ToolContextType } from "components/services/ToolContext";
import { keys, pick, omit, toPairs } from "lodash";

/**
 * Interface for the URL search params
 */
interface URLParams {
  savekey?: string;
  ver?: string;
  [key: string]: string | undefined;
}

/**
 * Interface for Redux state structure
 */
interface ReduxState {
  ui: Record<string, any>;
  container: {
    areas: Record<string, any>;
    [key: string]: any;
  };
}

/**
 * Interface for key mappings
 */
interface KeyMapping {
  uiKeys: string[];
  containerKeys: string[];
  areaKeys: string[];
  areaOmitKeys: string[];
}

/**
 * Interface for the payload structure
 */
interface PayloadData {
  tool: string;
  toolversion: string;
  date: Date;
  locale: string;
  savekey?: string;
  revision?: string;
  areaList: string[];
  areas: Record<string, any>;
  [key: string]: any;
}

/**
 * Create a payload for save or export operations
 * 
 * @param keyMap - Optional key mapping to control what's included in the payload
 * @returns The payload data object
 */
const Payload = (keyMap: string | null = null): PayloadData => {
  // Get tool context - will use for configuration
  const context = useContext(ToolContext) as ToolContextType;

  // Extract constants from context
  const { fullName: tool, ver: toolversion } = context.constants.tool;
  const { extraSaveParameters = (value: any) => ({}) } = context.constants;
  
  // Get key mappings for filtering data
  const defaultMapping: KeyMapping = { uiKeys: [], containerKeys: [], areaKeys: [], areaOmitKeys: [] };
  const { uiKeys = [], containerKeys = [], areaKeys = [], areaOmitKeys = [] } = 
    (keyMap ? (context.constants as any)[keyMap] || defaultMapping : defaultMapping);
  
  // Get URL parameters
  const { search } = useLocation();
  const params = Object.fromEntries(new URLSearchParams(search)) as URLParams;
  const { savekey, ver } = params;

  // Get Redux store
  const store = useStore();

  // Create payload function that captures current state
  const payload = useCallback((): PayloadData => {
    const state = store.getState() as ReduxState;
    
    return {
      // Basic metadata
      tool: tool.toLowerCase().replace(/ /g, ''),
      toolversion: toolversion,
      date: new Date(),
      locale: Intl.DateTimeFormat().resolvedOptions().locale,
      savekey: savekey,
      revision: ver,

      // UI state (filtered by uiKeys)
      ...pick(state.ui, uiKeys),

      // Container state (filtered by containerKeys)
      ...pick(state.container, containerKeys),

      // List of area IDs
      areaList: keys(state.container.areas),

      // Area data (filtered by areaKeys and areaOmitKeys)
      areas: areaKeys.length > 0 ? {
        ...toPairs(state.container.areas).reduce((acc: Record<string, any>, [key, value]) => {
          if (value) {
            acc[key] = { 
              ...omit(pick(value, areaKeys), areaOmitKeys),
              ...extraSaveParameters(value) 
            };
          }
          return acc;
        }, {})
      } : {},
    };
  }, [uiKeys, containerKeys, areaKeys, areaOmitKeys, tool, toolversion, savekey, ver, store, extraSaveParameters]);

  // Call the payload function to generate the data
  return payload();
};

export default Payload;
